import {TimeSlot} from '@wix/ambassador-table-reservations-v1-time-slot/types'
import {ReservationLocation} from '@wix/ambassador-table-reservations-v1-reservation-location/types'
import {ControllerFlowAPI} from '@wix/yoshi-flow-editor'

import {createStorageProvider} from '../../utils/storageContext'
import {RequestStatus, wrapRequest} from '../../utils/wrapRequest'
import {noop} from '../../utils/helpers'
import {getRegionalSettings} from '../../utils/flowAPI'
import {reservationLocationsService} from '../../services/reservationLocationsService'
import {getReservationLocationsMock} from '../../editor/editorMocks/getReservationLocationsMock'
import {getDayTimeSlotsMock} from '../../editor/editorMocks/getDayTimeSlotsMock'
import {goToNewReservation, goToReservationDetails} from '../../utils/navigation'
import {ApprovalTextEditorState} from '../../utils/constants'
import {getIsSiteBusinessPremium} from '../../services/premiumFeaturesService'
import {timeSlotsService} from '../../services/timeSlotsService'

interface ReservationAddOnData {
  regionalSettings?: string
  compId: string
  getReservationLocationsStatus: RequestStatus
  getDayTimeSlotsStatus: RequestStatus
  reservationLocations: ReservationLocation[]
  approvalTextEditorState: ApprovalTextEditorState
  fitToContentHeight: boolean
  isSiteBusinessPremium: boolean
  dayTimeSlots: TimeSlot[]
}

interface ReservationAddOnActions {
  getReservationLocations: () => Promise<ReservationLocation[] | undefined>
  goToReservationsPage: (params: {
    reservationData: {
      reservationLocationId: string
      partySize: number
      startDate: Date
    }
  }) => void
  goToDetailsPage: (params: {
    reservationData: {
      startDate: Date
      partySize: number
      reservationLocationId: string
    }
  }) => void
  getIsSiteBusinessPremium: () => Promise<boolean | undefined>
  getDayTimeSlots: (params: {
    reservationLocationId: string
    partySize: number
    date: Date
    timeSlotInterval?: number | null
    timeZone?: string | null
  }) => Promise<TimeSlot[] | undefined>
}

export interface ReservationAddOnStorage extends ReservationAddOnData, ReservationAddOnActions {}

const defaultReservationAddOnStorage: ReservationAddOnStorage = {
  getReservationLocationsStatus: RequestStatus.DEFAULT,
  getDayTimeSlotsStatus: RequestStatus.DEFAULT,
  reservationLocations: [],
  dayTimeSlots: [],
  goToReservationsPage: noop,
  goToDetailsPage: noop,
  getReservationLocations: noop,
  compId: '',
  approvalTextEditorState: ApprovalTextEditorState.unknown,
  fitToContentHeight: true,
  isSiteBusinessPremium: false,
  getIsSiteBusinessPremium: noop,
  getDayTimeSlots: noop,
}

const {withStorageProvider, useStorage} = createStorageProvider(defaultReservationAddOnStorage)

const initReservationAddOnStorage = (flowAPI: ControllerFlowAPI): ReservationAddOnStorage => ({
  ...defaultReservationAddOnStorage,
  regionalSettings: getRegionalSettings(flowAPI),
  getReservationLocations: wrapRequest(
    flowAPI,
    reservationLocationsService.getReservationLocations,
    'reservationLocations',
    'getReservationLocationsStatus',
  ),
  compId: flowAPI.controllerConfig.compId,
  goToReservationsPage: ({reservationData}) => goToNewReservation({flowAPI, reservationData}),
  goToDetailsPage: ({reservationData}) =>
    goToReservationDetails({
      flowAPI,
      reservationData,
    }),
  getIsSiteBusinessPremium: wrapRequest(
    flowAPI,
    getIsSiteBusinessPremium,
    'isSiteBusinessPremium',
    'getIsSiteBusinessPremiumStatus',
  ),
  getDayTimeSlots: wrapRequest(
    flowAPI,
    timeSlotsService.getDayTimeSlots,
    'dayTimeSlots',
    'getDayTimeSlotsStatus',
  ),
})

const mockReservationAddOnStorage = (flowAPI: ControllerFlowAPI): ReservationAddOnStorage => {
  const initiatedStorage = initReservationAddOnStorage(flowAPI)

  return {
    ...initiatedStorage,
    compId: flowAPI.controllerConfig.compId,
    getReservationLocations: wrapRequest(
      flowAPI,
      getReservationLocationsMock,
      'reservationLocations',
      'getReservationLocationsStatus',
    ),
    getDayTimeSlots: wrapRequest(
      flowAPI,
      getDayTimeSlotsMock,
      'dayTimeSlots',
      'getDayTimeSlotsStatus',
    ),
  }
}

export {
  initReservationAddOnStorage,
  mockReservationAddOnStorage,
  withStorageProvider as withReservationAddOnStorageProvider,
  useStorage as useReservationAddonStorage,
}
